import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';

const AUTO_RETRY_SECONDS = 5;

/**
 * Shown instead of silently bouncing to /login when the backend itself is
 * unreachable (network error, or a 502/503/504 gateway error) - distinct
 * from an actual invalid/expired session. See AuthContext's
 * isServerUnreachable / serverDown.
 *
 * `onRetry` is also called automatically by AuthContext the moment the
 * server is confirmed reachable again (api.js's reconnect polling) - the
 * countdown here is a second, independent attempt so the page recovers on
 * its own even if that signal is missed, same as a typical "maintenance
 * page" pattern.
 */
const ServerMaintenance = ({ onRetry }) => {
    const [secondsLeft, setSecondsLeft] = useState(AUTO_RETRY_SECONDS);
    const [retrying, setRetrying] = useState(false);

    useEffect(() => {
        if (secondsLeft <= 0) {
            setRetrying(true);
            Promise.resolve(onRetry?.()).finally(() => {
                setRetrying(false);
                setSecondsLeft(AUTO_RETRY_SECONDS);
            });
            return undefined;
        }
        const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secondsLeft]);

    const handleManualRetry = () => {
        setRetrying(true);
        Promise.resolve(onRetry?.()).finally(() => {
            setRetrying(false);
            setSecondsLeft(AUTO_RETRY_SECONDS);
        });
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: 3,
                bgcolor: '#F8FAFC',
            }}
        >
            <Box
                sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    bgcolor: '#FEE2E2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                }}
            >
                <CloudOffIcon sx={{ fontSize: 36, color: '#DC2626' }} />
            </Box>

            <Typography variant="h5" fontWeight={700} gutterBottom>
                Server Under Maintenance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mb: 3 }}>
                The application server is temporarily unavailable. This page will
                reconnect on its own — your session is still active, nothing has been lost.
            </Typography>

            <Typography variant="caption" color="text.disabled" sx={{ mb: 2 }}>
                {retrying ? 'Checking…' : `Retrying automatically in ${secondsLeft}s`}
            </Typography>

            <Button
                variant="contained"
                onClick={handleManualRetry}
                disabled={retrying}
                startIcon={retrying ? <CircularProgress size={16} color="inherit" /> : null}
            >
                Try again now
            </Button>
        </Box>
    );
};

export default ServerMaintenance;
